import { useState } from 'react';
import { useData } from '../contexts/DataContext';
import { FileSpreadsheet, Loader2, CheckCircle, AlertCircle, Download } from 'lucide-react';
import { importFromExcel } from '../utils/excelImport';
import { downloadTemplate } from '../utils/excelExport';
import type { TimeEntry } from '../types';

export function ImportExcel() {
  const { projects, locations, saveEntry } = useData();
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<{type: 'idle' | 'success' | 'error', message: string}>({type: 'idle', message: ''});
  const [templateMonth, setTemplateMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const handleDownloadTemplate = () => {
    downloadTemplate(projects, templateMonth);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportStatus({type: 'idle', message: 'Reading and parsing Excel file...'});

    try {
      const buffer = await file.arrayBuffer();
      
      // Parse file contents
      const { entries, projects: parsedProjects, locations: parsedLocations } = importFromExcel(buffer, projects, locations);

      if (entries.length === 0) {
        throw new Error('No valid time entries found in the Excel file.');
      }

      setImportStatus({type: 'idle', message: 'Synchronizing projects and locations...'});

      // Keep maps of temporary IDs to real database IDs
      const projectIdMap = new Map<string, string>();
      const locationIdMap = new Map<string, string>();

      // 1. Map projects
      for (const parsedProj of parsedProjects) {
        const existing = projects.find(p => p.name.toLowerCase() === parsedProj.name.toLowerCase());
        if (existing) {
          projectIdMap.set(parsedProj.id, existing.id);
        }
      }

      // 2. Map locations
      for (const parsedLoc of parsedLocations) {
        const existing = locations.find(l => l.name.toLowerCase() === parsedLoc.name.toLowerCase());
        if (existing) {
          locationIdMap.set(parsedLoc.id, existing.id);
        }
      }

      setImportStatus({type: 'idle', message: `Importing ${entries.length} time entries...`});

      // 3. Map entry references to actual database IDs and save entries
      let importedCount = 0;
      for (const entry of entries) {
        const mappedLocationId = locationIdMap.get(entry.locationId) || '';
        const mappedMainProjectId = projectIdMap.get(entry.mainProjectId) || '';
        
        const mappedProjects = entry.projects
          .map(p => ({
            ...p,
            projectId: projectIdMap.get(p.projectId) || ''
          }))
          .filter(p => p.projectId);

        if (mappedProjects.length === 0) continue;

        const mappedEntry: TimeEntry = {
          ...entry,
          locationId: mappedLocationId,
          mainProjectId: mappedMainProjectId || mappedProjects[0].projectId,
          projects: mappedProjects
        };

        await saveEntry(mappedEntry);
        importedCount++;
      }

      setImportStatus({
        type: 'success',
        message: `Successfully imported ${importedCount} entries, verified projects and locations!`
      });

    } catch (err: unknown) {
      console.error(err);
      const msg = err instanceof Error ? err.message : 'Failed to parse Excel file.';
      setImportStatus({ type: 'error', message: msg });
    } finally {
      setIsImporting(false);
      if (e.target) e.target.value = '';
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
      <h2 className="text-lg font-semibold text-slate-800">Import Excel (Legacy Onboarding)</h2>
      <div className="max-w-md space-y-4">
        <p className="text-sm text-slate-600">
          Upload an exported <code>TimeReport_YYYY-MM.xlsx</code> file to migrate historical project worklogs. We will automatically create missing projects and locations, merging work blocks for the same day.
        </p>
        
        <div className="flex flex-col gap-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Upload Filled Timesheet</label>
          <label className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all shadow-sm cursor-pointer border ${isImporting ? 'bg-slate-100 text-slate-400 border-slate-200' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200 hover:scale-[1.02] active:scale-[0.98]'}`}>
            {isImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            {isImporting ? 'Importing Excel...' : 'Select and Import Excel File'}
            <input 
              type="file" 
              accept=".xlsx,.xls" 
              className="hidden" 
              onChange={handleFileUpload}
              disabled={isImporting}
            />
          </label>
        </div>

        <div className="border-t border-slate-100 pt-3 flex flex-col gap-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Get Blank Template</label>
          <div className="flex gap-2">
            <input 
              type="month" 
              value={templateMonth} 
              onChange={e => setTemplateMonth(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500 outline-none bg-white text-sm flex-1"
            />
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm border bg-white hover:bg-slate-50 text-slate-700 border-slate-200 hover:scale-[1.02] active:scale-[0.98]"
            >
              <Download className="w-4 h-4 text-slate-500" />
              Download
            </button>
          </div>
        </div>

        {importStatus.type === 'idle' && isImporting && (
          <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-100 animate-pulse">
            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            {importStatus.message}
          </div>
        )}

        {importStatus.type === 'success' && (
          <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 p-3 rounded-lg border border-emerald-100 animate-in fade-in zoom-in-95 duration-200">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            {importStatus.message}
          </div>
        )}

        {importStatus.type === 'error' && (
          <div className="flex items-center gap-2 text-sm text-rose-600 bg-rose-50 p-3 rounded-lg border border-rose-100 animate-in fade-in zoom-in-95 duration-200">
            <AlertCircle className="w-4 h-4 text-rose-500" />
            {importStatus.message}
          </div>
        )}
      </div>
    </section>
  );
}
